import PageLayout from "../../../../components/page/PageLayout";
import CartReview from "../cart/stage/CartReview";
import {useEffect, useRef, useState} from "react";
import CartCheckout from "../cart/stage/CartCheckout";
import DialogLoading from "../../general/dialog/DialogLoading";
import CartConfirmation from "../cart/stage/CartConfirmation";
import CartEmpty from "../cart/stage/CartEmpty";
import useCheckoutStore from "../../../cart/store/checkoutStore";
import useValueChangeListener from "../../../services/state/general/useValueChangeListener";
import useSession from "../../../services/state/session/useSession";
import {useSearchParams} from "react-router-dom";
import {PaymentPending} from "./PagePaymentPending";

const REVIEW = "R";
const CHECKOUT = "C";
const AFTER_CHECKOUT = "AF";

const EmptySwitcher = ({children}) => {

    const count = useCheckoutStore(state => state.products?.lines?.length);
    const hasProducts = count > 0;

    if(hasProducts) {
        return <>{children}</>
    } else {
        return <CartEmpty />
    }
}

const Prepare = ({ children, skipPrepare }) => {

    const session = useSession();
    const loading = useCheckoutStore(state => state.loading?.prepareCheckout);
    const prepareCart = useCheckoutStore(state => state.prepareCheckout);
    const completedOrder = useCheckoutStore(state => state.completedOrder);

    useValueChangeListener(() => {
        if(!completedOrder && !skipPrepare) prepareCart(session);
    }, [1], [])

    return (
        <>
            <DialogLoading loading={loading} message={"Preparing Cart"} />
            <div
                style={{
                    filter: loading ? "blur(4px)" : "none",
                    pointerEvents: loading ? "none" : "auto",
                    transition: "filter 0.2s ease"
                }}
            >
                {children}
            </div>
        </>
    )
}

const ThePage = () => {

    const completedOrder = useCheckoutStore(state => state.completedOrder);
    const clearConfirmation = useCheckoutStore(state => state.clearConfirmation);
    const paymentTransactionId = useCheckoutStore(state => state.paymentTransactionId);
    const [searchParams, setSearchParams] = useSearchParams();

    const consentParam = searchParams.get("consent") || searchParams.get("consentid");
    const consentTokenRef = useRef(consentParam);
    const [isPaymentPending] = useState(!!(consentParam && paymentTransactionId));

    // Strip bank callback params from URL after reading them
    useEffect(() => {
        if (consentParam) {
            searchParams.delete("consent");
            searchParams.delete("consentid");
            searchParams.delete("institution");
            searchParams.delete("user-uuid");
            searchParams.delete("application-user-id");
            setSearchParams(searchParams, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const orderIsComplete = false;
    const [ cartState, setCartState ] = useState(orderIsComplete ? AFTER_CHECKOUT : REVIEW);

    const completedOrderRef = useRef(completedOrder);
    completedOrderRef.current = completedOrder;
    const clearTimerRef = useRef(null);

    useEffect(() => {
        if (clearTimerRef.current) {
            clearTimeout(clearTimerRef.current);
            clearTimerRef.current = null;
        }
        return () => {
            clearTimerRef.current = setTimeout(() => {
                if(completedOrderRef.current) clearConfirmation();
            }, 200);
        };
    }, [clearConfirmation]);

    return (
        <Prepare skipPrepare={isPaymentPending}>
            {completedOrder &&
                <CartConfirmation completedOrder={completedOrder} />
            }
            {!completedOrder &&
                <>
                    {isPaymentPending &&
                        <PaymentPending consentToken={consentTokenRef.current} />
                    }
                    <div style={isPaymentPending ? {
                        filter: "blur(4px)",
                        pointerEvents: "none",
                        transition: "filter 0.2s ease"
                    } : undefined}>
                        <EmptySwitcher>
                            {cartState === REVIEW &&
                                <CartReview onNext={() => setCartState(CHECKOUT)} />
                            }
                            {cartState === CHECKOUT &&
                                <CartCheckout />
                            }
                        </EmptySwitcher>
                    </div>
                </>
            }
        </Prepare>
    )
}

const PageCartReview = () => {

    return (
        <PageLayout requireSignIn={true}>
            <ThePage />
        </PageLayout>
    )
}

export default PageCartReview;
